import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Flame, Loader2, LogOut, Download, RefreshCw } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { apiGet } from "@/lib/api";
import { toast } from "sonner";

const withCreds = { withCredentials: true };

const StatCard = ({ label, value, sub }) => (
  <div className="glass rounded-2xl p-5">
    <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A99B5]">{label}</p>
    <p className="font-display text-3xl text-gold-gradient mt-2">{value}</p>
    {sub && <p className="text-xs text-[#8A99B5] mt-1">{sub}</p>}
  </div>
);

const Admin = () => {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [candles, setCandles] = useState([]);
  const [stats, setStats] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const clearDemo = async () => {
    if (!confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 5000);
      return;
    }
    try {
      const res = await fetch("/api/admin/demo/clear", { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      toast.success(`Cleared ${data.deleted} demo order${data.deleted === 1 ? "" : "s"} — ${data.seats_restored} seats back in the open.`);
      setConfirmClear(false);
      await loadAll();
    } catch {
      toast.error("Couldn't clear demo orders — try again.");
      setConfirmClear(false);
    }
  };

  const loadAll = async () => {
    try {
      const [o, c, s] = await Promise.all([
        apiGet("/api/admin/orders"),
        apiGet("/api/admin/candles"),
        apiGet("/api/admin/stats"),
      ]);
      setOrders(o.orders || []);
      setCandles(c.candles || []);
      setStats(s);
      setAuthed(true);
    } catch {
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    apiGet("/api/auth/me", withCreds)
      .then(loadAll)
      .catch(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Login failed");
      }
      setPassword("");
      await loadAll();
      toast.success("Welcome back, Susan.");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={30} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-5">
        <LogoMark size={54} />
        <h1 className="font-display text-3xl text-[#FDFBF7] mt-7">Susan's Box Office</h1>
        <p className="text-sm text-[#8A99B5] font-light mt-2">Private admin access only.</p>
        <form onSubmit={login} className="glass rounded-3xl p-7 w-full max-w-sm mt-8 space-y-5">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-[#8A99B5]">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="mt-2 w-full bg-[#131B36] border border-white/10 rounded-xl px-4 py-3 text-sm text-[#F8F9FA] focus:outline-none focus:border-[#D4AF37]/60"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-[#8A99B5]">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="mt-2 w-full bg-[#131B36] border border-white/10 rounded-xl px-4 py-3 text-sm text-[#F8F9FA] focus:outline-none focus:border-[#D4AF37]/60"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="btn-gold w-full rounded-full py-3.5 text-sm font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : null} Sign In
          </button>
        </form>
        <Link to="/" className="text-xs text-[#8A99B5] hover:text-[#F3D068] mt-6 transition-colors">
          ← Back to the event
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-40 py-4">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={34} />
            <span className="font-cinzel text-gold-gradient tracking-[0.14em] text-sm">SUSAN'S BOX OFFICE</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/api/admin/orders.csv"
              data-testid="admin-csv-link"
              className="btn-ghost-gold rounded-full px-5 py-2.5 text-xs font-medium inline-flex items-center gap-2"
            >
              <Download size={14} /> Export CSV
            </a>
            <button
              onClick={loadAll}
              data-testid="admin-refresh-button"
              className="btn-ghost-gold rounded-full px-4 py-2.5 text-xs font-medium inline-flex items-center gap-2"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={clearDemo}
              data-testid="admin-clear-demo-button"
              className={`rounded-full px-5 py-2.5 text-xs font-bold inline-flex items-center gap-2 transition-all ${
                confirmClear
                  ? "bg-red-500/90 text-white hover:bg-red-500"
                  : "btn-ghost-gold"
              }`}
            >
              {confirmClear ? "Really clear? Click again" : "Clear Demo Orders"}
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="text-[#8A99B5] hover:text-[#F3D068] transition-colors p-2"
              aria-label="Sign out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-12">
        <h1 className="font-display text-4xl text-[#FDFBF7]">
          Tonight's <em className="text-gold-gradient italic">box office</em>
        </h1>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-9">
            <StatCard label="Orders" value={stats.orders} />
            <StatCard label="Revenue" value={`$${stats.revenue.toLocaleString()}`} />
            <StatCard label="Seats sold" value={stats.seats_sold} sub={`of ${stats.seats_total}`} />
            <StatCard label="Seats remaining" value={stats.seats_remaining} />
            <StatCard label="Candles lit" value={stats.candles} />
          </div>
        )}

        <h2 className="font-display text-2xl text-[#FDFBF7] mt-14 flex items-center gap-2.5">
          <Gift size={18} className="text-[#F3D068]" /> Orders & Gift Notes
        </h2>
        <div className="mt-6 overflow-x-auto border border-white/10 rounded-3xl" data-testid="admin-orders-table">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#8A99B5] border-b border-white/10">
                <th className="px-5 py-4">Order</th>
                <th className="px-5 py-4">Guest</th>
                <th className="px-5 py-4">Seats</th>
                <th className="px-5 py-4">Total</th>
                <th className="px-5 py-4">Gift</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-[#8A99B5] font-light">
                    No orders yet — the box office opens with your first reservation.
                  </td>
                </tr>
              )}
              {orders.map((o) => (
                <tr key={o.code} className="border-b border-white/5 last:border-0 align-top">
                  <td className="px-5 py-4">
                    <p className="font-cinzel text-[#F3D068] text-xs tracking-widest">
                      {o.code}
                      {o.demo && (
                        <span className="ml-2 text-[9px] uppercase tracking-[0.15em] text-[#8A99B5] border border-white/15 rounded-full px-2 py-0.5">
                          demo
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-[#8A99B5] mt-1">
                      {new Date(o.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-[#FDFBF7]">
                      {o.buyer.first_name} {o.buyer.last_name}
                    </p>
                    <p className="text-xs text-[#8A99B5] mt-0.5">{o.buyer.email}</p>
                  </td>
                  <td className="px-5 py-4 text-[#C5CEE0] text-xs leading-relaxed">
                    {o.tickets.map((t) => `${t.zone_label} ${t.row}${t.seat_number}`).join(", ")}
                  </td>
                  <td className="px-5 py-4 text-[#FDFBF7] font-medium">${o.total}</td>
                  <td className="px-5 py-4">
                    {o.is_gift ? (
                      <div className="max-w-[220px]">
                        <p className="text-xs text-[#F3D068]">
                          For {o.recipient.name} ({o.recipient.email})
                        </p>
                        {o.recipient.message && (
                          <p className="text-xs text-[#8A99B5] italic mt-1 font-light">"{o.recipient.message}"</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-[#8A99B5]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="font-display text-2xl text-[#FDFBF7] mt-14 flex items-center gap-2.5">
          <Flame size={18} className="text-[#F59E0B]" /> Candle Dedications
        </h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-candles-list">
          {candles.length === 0 && (
            <p className="text-sm text-[#8A99B5] font-light">
              No candles lit yet — the Wall of Light is waiting.
            </p>
          )}
          {candles.map((c) => (
            <div key={c.id} className="border border-white/10 bg-[#131B36]/70 rounded-2xl p-5">
              <p className="text-[#FDFBF7] font-medium">
                {c.name}
                {c.seed && (
                  <span className="ml-2 text-[9px] uppercase tracking-[0.15em] text-[#8A99B5] border border-white/15 rounded-full px-2 py-0.5">
                    seed
                  </span>
                )}
              </p>
              {c.message && <p className="text-xs text-[#8A99B5] italic mt-1.5 font-light">"{c.message}"</p>}
              <p className="text-[10px] text-[#8A99B5] mt-3">
                {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Admin;
